<script setup lang="ts">
/**
 * Photos app preview, laid out like the players (player-web render/photos.ts, Android PhotosView.kt): the feed's
 * pictures as a slideshow, a wall whose tiles change one at a time, or instant photos. Thumbnails (`live` off) hold
 * still on the first photos.
 */
import { computed, onBeforeUnmount, ref, shallowRef, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { photoList, wallTiles, type DataFeed, type Photo, type PhotosWidget } from '@codot/contracts';
import { loadFeed } from './ChartPreview.vue';

const props = withDefaults(defineProps<{ config: PhotosWidget; live?: boolean }>(), { live: true });
const { t } = useI18n();
const feed = shallowRef<DataFeed | null>(null);
const failed = ref(false);
watch(
  () => props.config.feedId,
  async (id) => {
    feed.value = null;
    failed.value = false;
    if (!id) return;
    try {
      feed.value = await loadFeed(id);
    } catch {
      failed.value = true;
    }
  },
  { immediate: true },
);
// Shuffle once per feed/settings change (not on every redraw).
const photos = computed<Photo[]>(() => (feed.value?.table ? photoList(feed.value.table, props.config) : []));

const tick = ref(0);
let timer = 0;
watch(
  () => [props.live, props.config.slideSeconds, photos.value.length] as const,
  ([live, secs, n]) => {
    clearInterval(timer);
    tick.value = 0;
    if (live && n > 1) timer = window.setInterval(() => tick.value++, secs * 1000);
  },
  { immediate: true },
);
onBeforeUnmount(() => clearInterval(timer));

const current = computed(() => (photos.value.length ? { photo: photos.value[tick.value % photos.value.length]!, n: tick.value } : null));
const tiles = computed(() => wallTiles(props.config, photos.value.length));
const cols = computed(() => Math.min(props.config.wallColumns, tiles.value));
/** Tile i shows photo i at first; each tick the next tile in turn moves on to the next unseen photo. */
const wall = computed(() => {
  const n = tiles.value;
  const list = photos.value;
  const out = list.slice(0, n).map((p) => ({ p, v: 0 }));
  if (list.length > n) for (let k = 0; k < tick.value; k++) out[k % n] = { p: list[(n + k) % list.length]!, v: k + 1 };
  return out;
});
const message = computed(() =>
  !props.config.feedId ? t('appsCat.photosPickFeed') : failed.value ? t('appsCat.chartNoFeed') : feed.value && !feed.value.table ? t('appsCat.chartNoTable') : feed.value ? t('appsCat.photosNone') : '',
);
const pad = computed(() => (props.config.style === 'wall' ? 'min(2cqh, 2cqw)' : props.config.style === 'polaroid' ? 'min(4cqh, 4cqw)' : '0'));
</script>

<template>
  <div class="flex h-full w-full flex-col overflow-hidden [container-type:size]" :dir="config.direction" :style="{ background: config.background, color: config.foreground, padding: pad }">
    <p v-if="config.title" class="shrink-0 truncate font-bold" :style="{ fontSize: 'min(8cqh, 5.5cqw)', margin: config.style === 'slideshow' ? 'min(3cqh, 3cqw) min(4cqh, 4cqw)' : '0 0 min(2.5cqh, 2.5cqw)' }">{{ config.title }}</p>
    <div class="relative min-h-0 flex-1">
      <p v-if="!photos.length" class="flex h-full items-center justify-center px-4 text-center opacity-75" :style="{ fontSize: 'min(6cqh, 4.5cqw)' }">{{ message }}</p>
      <!-- Wall -->
      <div v-else-if="config.style === 'wall'" class="grid h-full" :style="{ gridTemplateColumns: `repeat(${cols}, 1fr)`, gridTemplateRows: `repeat(${Math.ceil(tiles / cols)}, 1fr)`, gap: 'min(1.5cqh, 1.5cqw)' }">
        <div v-for="(w, i) in wall" :key="i" class="relative overflow-hidden bg-gray-500/15" style="border-radius: min(1.2cqh, 1.2cqw)">
          <Transition name="ph">
            <div :key="w.v + w.p.url" class="absolute inset-0">
              <img :src="w.p.url" alt="" class="block h-full w-full" :style="{ objectFit: config.fit }" loading="lazy" />
              <p v-if="w.p.caption" class="ph-cap truncate font-medium" style="font-size: min(3.2cqh, 2.4cqw)">{{ w.p.caption }}</p>
            </div>
          </Transition>
        </div>
      </div>
      <!-- Slideshow / instant photo -->
      <Transition v-else name="ph">
        <div v-if="current && config.style === 'polaroid'" :key="current.n" class="absolute inset-0 flex items-center justify-center">
          <div
            class="flex flex-col bg-white text-[#222]"
            :style="{ height: '94%', aspectRatio: '0.84', maxWidth: '94%', padding: '3.5% 3.5% 0', boxShadow: '0 1.5cqh 4cqh rgba(0,0,0,.35)', transform: `rotate(${current.n % 2 ? -2.5 : 2}deg)` }"
          >
            <img :src="current.photo.url" alt="" class="block min-h-0 w-full flex-1 bg-[#eee]" :style="{ objectFit: config.fit }" />
            <p class="flex items-center justify-center truncate text-center font-medium" style="height: 16%; font-size: min(5.5cqh, 4cqw)">{{ current.photo.caption }}</p>
          </div>
        </div>
        <div v-else-if="current" :key="current.n" class="absolute inset-0">
          <img :src="current.photo.url" alt="" class="block h-full w-full" :style="{ objectFit: config.fit }" />
          <p v-if="current.photo.caption" class="ph-cap truncate font-medium" style="font-size: min(5cqh, 3.6cqw)">{{ current.photo.caption }}</p>
        </div>
      </Transition>
    </div>
  </div>
</template>

<style scoped>
.ph-cap {
  position: absolute;
  inset-inline: 0;
  bottom: 0;
  color: #fff;
  padding: 1.6em 0.8em 0.6em;
  background: linear-gradient(transparent, rgb(0 0 0 / 0.65));
}
.ph-enter-active,
.ph-leave-active {
  transition: opacity 0.8s ease;
}
.ph-enter-from,
.ph-leave-to {
  opacity: 0;
}
</style>
